import type { BridgePin, BridgePinMedia } from './MapBridge';

interface MapPoiResponse {
    id: number;
    kind: 'poi' | 'story' | 'species';
    title: string;
    description?: string;
    category?: string;
    subcategory?: string;
    color?: string;
    lat: number;
    lng: number;
    thumbnail_url?: string;
    icon_url?: string;
    wpt_node?: number;
    media?: Array<{
        id: number;
        role: string;
        mime_type: string;
        url: string;
        thumbnail_url?: string;
    }>;
}

function currentLanguage(): string {
    const requested = new URLSearchParams(window.location.search).get('lang');
    return (requested || document.documentElement.lang || navigator.language || 'de')
        .toLowerCase()
        .split('-', 1)[0];
}

function isMapPoi(value: unknown): value is MapPoiResponse {
    const item = value as Partial<MapPoiResponse> | null;
    return !!item
        && Number.isFinite(item.id)
        && Number.isFinite(item.lat)
        && Number.isFinite(item.lng)
        && typeof item.title === 'string';
}

/** Load the public, server-sanitized Tscheppaschlucht content contract. */
export async function loadTscheppaMapPins(signal?: AbortSignal): Promise<BridgePin[]> {
    const response = await fetch(
        `/presenter-api/api/v1/maps/tscheppa/pois?lang=${encodeURIComponent(currentLanguage())}`,
        { signal, credentials: 'omit', headers: { Accept: 'application/json' } },
    );
    if (!response.ok) throw new Error(`Tscheppa POI feed ${response.status}`);
    const payload: unknown = await response.json();
    if (!Array.isArray(payload)) throw new Error('Tscheppa POI feed has an invalid format');

    return payload.filter(isMapPoi).map((item): BridgePin => ({
        id: item.id,
        kind: item.kind || 'poi',
        title: item.title,
        description: item.description || '',
        category: item.category || 'poi',
        subcategory: item.subcategory,
        color: item.color,
        lat: item.lat,
        lng: item.lng,
        thumbnailUrl: item.thumbnail_url,
        iconUrl: item.icon_url,
        wptNode: item.wpt_node,
        media: (item.media || []).map((media): BridgePinMedia => ({
            id: media.id,
            role: media.role,
            mimeType: media.mime_type,
            url: media.url,
            thumbnailUrl: media.thumbnail_url,
        })),
    }));
}
